import type { ContentArtwork } from "./contracts";
import type { UiCopy } from "./i18n";
import { renderReviewMarkdown } from "./review-markdown";

export class ArtworkDialog {
  readonly element: HTMLDialogElement;
  readonly #title: HTMLElement;
  readonly #kicker: HTMLElement;
  readonly #image: HTMLCanvasElement;
  readonly #metadata: HTMLElement;
  readonly #review: HTMLElement;
  readonly #copy: UiCopy;
  readonly #onCloseRequest: () => void;
  #invoker: HTMLElement | null = null;

  constructor(element: HTMLDialogElement, copy: UiCopy, onCloseRequest: () => void) {
    this.element = element;
    this.#copy = copy;
    this.#onCloseRequest = onCloseRequest;
    this.#title = required(element, "[data-artwork-title]");
    this.#kicker = required(element, "[data-artwork-kicker]");
    this.#image = required<HTMLCanvasElement>(element, "[data-artwork-image]");
    this.#metadata = required(element, "[data-artwork-metadata]");
    this.#review = required(element, "[data-artwork-review]");
    required<HTMLButtonElement>(element, "[data-close-artwork]").addEventListener("click", () => {
      element.close();
      this.#onCloseRequest();
    });
    this.#image.addEventListener("click", () => {
      void this.#toggleFullscreen();
    });
    this.#image.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      void this.#toggleFullscreen();
    });
    document.addEventListener("fullscreenchange", () => this.#updateFullscreenLabel());
    element.addEventListener("close", () => {
      this.#invoker?.focus();
      this.#invoker = null;
    });
  }

  open(
    artwork: ContentArtwork,
    roomTitle: string,
    image: CanvasImageSource,
    invoker?: HTMLElement,
  ): void {
    this.#invoker = invoker ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    this.#title.textContent = artwork.title;
    this.#kicker.textContent = this.#copy.artworkKicker(artwork.route_order, 8, roomTitle);
    this.#image.width = artwork.display_image.width;
    this.#image.height = artwork.display_image.height;
    this.#image.dataset.fullscreenViewLabel = this.#copy.viewFullscreen(
      artwork.title,
      artwork.author,
    );
    this.#image.dataset.fullscreenExitLabel = this.#copy.exitFullscreen(artwork.title);
    this.#updateFullscreenLabel();
    const context = this.#image.getContext("2d");
    if (context === null) throw new Error("ARTWORK_DIALOG_CANVAS_UNAVAILABLE");
    context.clearRect(0, 0, this.#image.width, this.#image.height);
    context.drawImage(image, 0, 0, this.#image.width, this.#image.height);
    this.#image.dataset.rendered = "true";
    this.#metadata.replaceChildren(
      metadataRow(this.#copy.author, artwork.author),
      metadataRow(this.#copy.century, artwork.century),
      ...(artwork.styles.length > 0
        ? [metadataRow(this.#copy.styles, artwork.styles.join(", "))]
        : []),
    );
    this.#review.innerHTML = renderReviewMarkdown(artwork.review_markdown);
    this.#review.scrollTop = 0;
    if (!this.element.open) this.element.showModal();
    required<HTMLButtonElement>(this.element, "[data-close-artwork]").focus();
  }

  async #toggleFullscreen(): Promise<void> {
    if (document.fullscreenElement === this.#image) {
      await document.exitFullscreen();
      return;
    }
    await this.#image.requestFullscreen();
  }

  #updateFullscreenLabel(): void {
    const label =
      document.fullscreenElement === this.#image
        ? (this.#image.dataset.fullscreenExitLabel ?? "")
        : (this.#image.dataset.fullscreenViewLabel ?? "");
    this.#image.setAttribute("aria-label", label);
    this.#image.title = label;
  }
}

function metadataRow(label: string, value: string): HTMLElement {
  const wrapper = document.createElement("div");
  const term = document.createElement("dt");
  const definition = document.createElement("dd");
  term.textContent = label;
  definition.textContent = value;
  wrapper.append(term, definition);
  return wrapper;
}

function required<T extends Element = HTMLElement>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`UI_ELEMENT_MISSING: ${selector}`);
  return element;
}
